import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

import { accessibilitySettings } from '../../config/accessibility-settings';
import { TAccessibilityOptionKey } from '../../models/accessibility.model';

export type TAccessibilityState = Record<TAccessibilityOptionKey, number>;

const initialState: TAccessibilityState = getInitialStateFromLocalStorage();

function getInitialStateFromLocalStorage(): TAccessibilityState {
    return Object.entries(accessibilitySettings).reduce<TAccessibilityState>((acc, entry) => {
        const [key, optionConfig] = entry;
        if (typeof window === 'undefined') {
            acc[key as TAccessibilityOptionKey] = optionConfig.baseValue;
        } else {
            const value =
                Number(localStorage.getItem(optionConfig.variableName)) || optionConfig.baseValue;
            document.documentElement.style.setProperty(optionConfig.variableName, value.toString());
            acc[key as TAccessibilityOptionKey] = value;
        }
        return acc;
    }, {} as TAccessibilityState);
}

const accessibilitySlice = createSlice({
    name: 'accessibility',
    initialState,
    reducers: {
        updateFontSize: (state, { payload }: PayloadAction<number>) => {
            localStorage.setItem('--fsm', payload.toString());
            state.fontSize = payload;
        },
        updateLineHeight: (state, { payload }: PayloadAction<number>) => {
            localStorage.setItem('--lhm', payload.toString());
            state.lineHeight = payload;
        },
        updateLetterSpacing: (state, { payload }: PayloadAction<number>) => {
            localStorage.setItem('--lsm', payload.toString());
            state.letterSpacing = payload;
        },
        updateWordSpacing: (state, { payload }: PayloadAction<number>) => {
            localStorage.setItem('--wsm', payload.toString());
            state.wordSpacing = payload;
        },
        updateParagraphMargin: (state, { payload }: PayloadAction<number>) => {
            localStorage.setItem('--pmm', payload.toString());
            state.paragraphMargin = payload;
        },
        updateContrast: (state, { payload }: PayloadAction<number>) => {
            localStorage.setItem('--cts', payload.toString());
            state.contrastMode = payload;
        },
        resetAllSettings: (state) => {
            Object.entries(accessibilitySettings).forEach((entry) => {
                const [key, optionConfig] = entry;
                localStorage.setItem(optionConfig.variableName, optionConfig.baseValue.toString());
                state[key as TAccessibilityOptionKey] = optionConfig.baseValue;
            });
        },
    },
});

export default accessibilitySlice.reducer;

export const {
    updateFontSize,
    updateLineHeight,
    updateLetterSpacing,
    updateWordSpacing,
    updateParagraphMargin,
    resetAllSettings,
    updateContrast,
} = accessibilitySlice.actions;

export const selectAccessibilityState = (state: RootState) => state.accessibility;
